import React from 'react';
import { CustomerModal } from '@/components/customers/customer-modal';
import { useMutation } from '@tanstack/react-query';
import { Customer } from '@shared/schema';

import { PageLayout } from '@/components/ui/page-layout';
import SimpleCalendar from '@/components/calendar/SimpleCalendar';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Search,
  Users,
  Mail,
  Phone,
  MapPin,
  Trash2,
  FileText,
  Star,
  PoundSterling,
  Copy,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Home,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { usePaginatedQuery } from '@/hooks/use-paginated-query';
import { PaginationControls } from '@/components/pagination';
import { fetchCustomers } from '@/lib/api/customers';

export default function CustomersPage() {
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [openCardId, setOpenCardId] = useState<number | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isCopied, setIsCopied] = useState<Record<string, boolean>>({});
  const [callInProgress, setCallInProgress] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [location, navigate] = useLocation();
  const { toast } = useToast();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Auto-open customer modal when navigating to /customers/new
  useEffect(() => {
    if (location === '/customers/new') {
      setCustomerModalOpen(true);
      // Navigate back to /customers to clean up the URL
      navigate('/customers', { replace: true });
    }
  }, [location, navigate]);

  // Function to copy text to clipboard
  const copyToClipboard = (text: string | null | undefined, id: string) => {
    if (!text) return;

    navigator.clipboard.writeText(text).then(
      () => {
        // Set the copied state for this specific ID
        setIsCopied((prev) => ({ ...prev, [id]: true }));

        // Reset after 2 seconds
        setTimeout(() => {
          setIsCopied((prev) => ({ ...prev, [id]: false }));
        }, 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Initiate a call to a customer
  const initiateCall = async (customerId: number, phoneNumber: string | null) => {
    if (!phoneNumber) {
      toast({
        title: 'Error',
        description: 'No phone number available for this customer',
        variant: 'destructive',
      });
      return;
    }

    try {
      setCallInProgress(true);
      const response = await apiRequest('POST', '/api/twilio/call', {
        customerId,
        customerPhone: phoneNumber,
      });

      toast({
        title: 'Call initiated',
        description: 'The call is being connected...',
      });

      // Wait a bit before allowing new calls
      setTimeout(() => {
        setCallInProgress(false);
      }, 3000);
    } catch (error) {
      console.error('Error initiating call:', error);
      toast({
        title: 'Call failed',
        description: 'Could not initiate the call. Please try again.',
        variant: 'destructive',
      });
      setCallInProgress(false);
    }
  };

  // Function to open address in Google Maps
  const openInGoogleMaps = (address: string) => {
    if (!address) return;

    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  // Use the new paginated query hook
  const {
    data: customers,
    isLoading,
    isFetching,
    pagination,
    invalidate,
  } = usePaginatedQuery<Customer>({
    queryKey: ['customers'],
    queryFn: fetchCustomers,
    filters: {
      search: debouncedSearch,
    },
    initialLimit: 50,
    limitOptions: [10, 25, 50, 100],
    storageKey: 'customers-pagination',
  });

  // Customer update mutation
  const updateCustomerMutation = useMutation({
    mutationFn: (data: { id: number; updates: any }) => {
      return apiRequest('PATCH', `/api/customers/${data.id}`, data.updates);
    },
    onSuccess: () => {
      invalidate(); // Use the invalidate function from the paginated query hook
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update customer: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Function to quickly update a customer's lead score
  const updateLeadScore = (customerId: number, score: number) => {
    updateCustomerMutation.mutate({
      id: customerId,
      updates: { leadScore: score },
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      try {
        // Use fetch directly to handle 204 No Content responses properly
        const response = await fetch(`/api/customers/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (response.ok) {
          return true; // Success, but no content to parse
        }

        // If there's an error with content, try to parse it
        if (response.status >= 400) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete customer');
          }
          throw new Error('Failed to delete customer');
        }

        return true;
      } catch (error) {
        console.error('Error deleting customer:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Customer deleted successfully',
      });
      invalidate(); // Use the invalidate function from the paginated query hook
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      setDeleteConfirmText('');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete customer',
        variant: 'destructive',
      });
    },
  });

  const handleDeleteClick = (customer: Customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
    setDeleteConfirmText('');
  };

  const handleConfirmDelete = () => {
    if (!customerToDelete) return;

    // Only proceed if the user entered the customer name correctly
    if (deleteConfirmText !== customerToDelete.name) {
      toast({
        title: 'Validation failed',
        description: 'Please type the customer name exactly to confirm deletion',
        variant: 'destructive',
      });
      return;
    }

    deleteMutation.mutate(customerToDelete.id);
  };

  // No need for client-side filtering anymore - the server handles it
  const displayCustomers = customers || [];

  return (
    <PageLayout>
      <div className="w-full max-w-6xl 2xl:max-w-[90rem] mx-auto space-y-6 fade-in ultra-wide-container">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-8 lg:pt-0">
          <div className="flex items-center gap-3">
            <div className="accent-icon-bg">
              <Users className="h-6 w-6 accent-icon-indigo" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-200">
              Customers
            </h1>
          </div>

          <CustomerModal
            onCustomerCreated={() => {
              toast({
                title: 'Success',
                description: 'Customer added successfully',
              });
              setCustomerModalOpen(false);
            }}
            triggerText="Add Customer"
            open={customerModalOpen}
            onOpenChange={setCustomerModalOpen}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex items-center space-x-2 bg-white dark:bg-slate-800/80 p-2 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 flex-1">
            <Search className="w-4 h-4 ml-2 text-slate-400 dark:text-slate-500" />
            <Input
              placeholder="Search customers by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 w-full"
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {/* For mobile */}
        {!isLoading && (
          <div className="sm:hidden space-y-4">
            {displayCustomers.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800/80 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="flex justify-center mb-3">
                  <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-full">
                    <Users className="h-6 w-6 text-slate-400 dark:text-slate-500" />
                  </div>
                </div>
                <p>No customers found</p>
                <p className="text-sm mt-1">Try adjusting your search</p>
              </div>
            ) : (
              displayCustomers?.map((customer) => (
                <div
                  key={customer.id}
                  className={`card-modern hover-float p-4 space-y-3 bg-white dark:bg-slate-800/90 relative cursor-pointer ${openCardId === customer.id ? 'ring-1 ring-primary' : ''}`}
                  onClick={() => setOpenCardId(openCardId === customer.id ? null : customer.id)}
                >
                  <div className="absolute top-2 right-2 flex gap-2">
                    {customer.opensolarId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-xs rounded-full bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            `https://app.opensolar.com/project/${customer.opensolarId}`,
                            '_blank'
                          );
                        }}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Open Solar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(customer);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete {customer.name}</span>
                    </Button>
                  </div>

                  {/* Top row with Name and Copy button */}
                  <div className="font-medium text-lg text-slate-800 dark:text-slate-200 flex items-center gap-2 pr-8">
                    <Users className="h-4 w-4 accent-icon-indigo" />
                    <div className="flex items-center">
                      <span>{customer.name}</span>
                      <button
                        type="button"
                        className="ml-1 focus:outline-none opacity-70 hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(customer.name, `name-${customer.id}`);
                        }}
                        title="Copy name"
                      >
                        {isCopied[`name-${customer.id}`] ? (
                          <CheckCheck className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3 text-slate-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Second row with star rating and deal value */}
                  <div className="flex items-center justify-between mt-1">
                    {/* Star rating */}
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((score) => (
                        <Star
                          key={score}
                          className={`h-3.5 w-3.5 ${
                            (customer.leadScore || 0) >= score
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                      ))}
                    </div>

                    {/* Deal value indicator */}
                    {customer.dealValue && customer.dealValue > 0 && (
                      <div
                        className="px-1.5 py-0.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300
                                     rounded text-xs font-medium flex items-center gap-1"
                      >
                        <PoundSterling className="h-3 w-3" />
                        <span>
                          £
                          {(customer.dealValue / 100).toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Essential Contact Info - Basic info visible in collapsed view */}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {/* Email indicator with copy button */}
                    {customer.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3 text-blue-500" />
                        <span>{customer.email}</span>
                        <button
                          type="button"
                          className="ml-1 focus:outline-none opacity-70 hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(customer.email, `email-${customer.id}`);
                          }}
                          title="Copy email"
                        >
                          {isCopied[`email-${customer.id}`] ? (
                            <CheckCheck className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3 text-slate-400" />
                          )}
                        </button>
                        {/* Show email preference indicator if explicitly set */}
                        {(customer?.additionalData?.prefer_email === 'yes' ||
                          customer?.additionalData?.contactPreference === 'Email' ||
                          (customer?.additionalData?.contactPreference &&
                            customer.additionalData.contactPreference
                              .toLowerCase()
                              .includes('email'))) && (
                          <span className="inline-block px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-sm text-[10px] font-medium ml-1">
                            Preferred
                          </span>
                        )}
                      </div>
                    )}

                    {/* Phone indicator with call button */}
                    {customer.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3 text-green-500" />
                        <span>{customer.phone}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1 py-0 text-[10px] text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30"
                          onClick={(e) => {
                            e.stopPropagation();
                            initiateCall(customer.id, customer.phone);
                          }}
                          disabled={callInProgress}
                        >
                          {callInProgress ? 'Calling...' : 'Call'}
                        </Button>
                        {/* Show phone preference indicator if explicitly set */}
                        {(customer?.additionalData?.prefer_phone === 'yes' ||
                          customer?.additionalData?.contactPreference === 'Phone' ||
                          (customer?.additionalData?.contactPreference &&
                            (customer.additionalData.contactPreference
                              .toLowerCase()
                              .includes('phone') ||
                              customer.additionalData.contactPreference
                                .toLowerCase()
                                .includes('call')))) && (
                          <span className="inline-block px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-sm text-[10px] font-medium ml-1">
                            Preferred
                          </span>
                        )}
                      </div>
                    )}

                    {/* Expand indicator */}
                    <button
                      type="button"
                      className="ml-auto text-xs text-slate-400 hover:text-slate-500 flex items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenCardId(openCardId === customer.id ? null : customer.id);
                      }}
                    >
                      {openCardId === customer.id ? (
                        <>
                          <ChevronUp className="h-3 w-3 mr-1" />
                          <span>Less</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          <span>More</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* EXPANDED DETAILS - Only shown when card is expanded */}
                  {openCardId === customer.id && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60 space-y-3">
                      {/* Address information with Google Maps link */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium">Address</div>
                        <div className="flex items-start text-xs text-muted-foreground">
                          <Home className="h-3 w-3 mr-1 mt-0.5 shrink-0" />
                          <div>
                            {(() => {
                              // Check for additionalData
                              const additionalData = customer.additionalData || {};

                              // First check for full_address
                              if (additionalData?.full_address) {
                                const fullAddress = additionalData.full_address;

                                // Add postcode if not already in full address
                                if (customer.postcode && !fullAddress.includes(customer.postcode)) {
                                  return `${fullAddress}, ${customer.postcode}`;
                                }

                                return fullAddress;
                              }

                              // Build address from parts as fallback
                              let address = '';

                              // Start with best available address field
                              if (additionalData?.street_address) {
                                address = additionalData.street_address;
                              } else if (additionalData?.address_line1) {
                                address = additionalData.address_line1;

                                // Add address_line2 if available
                                if (additionalData?.address_line2) {
                                  address += `, ${additionalData.address_line2}`;
                                }
                              } else if (customer.address) {
                                address = customer.address;
                              } else {
                                return 'No address provided';
                              }

                              // Add postcode if available and not already in address
                              let postcodeToUse = additionalData?.postcode || customer.postcode;

                              if (postcodeToUse && !address.includes(postcodeToUse)) {
                                address += `, ${postcodeToUse}`;
                              }

                              return address;
                            })()}
                          </div>
                        </div>

                        {/* Google Maps button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs h-7 mt-1 gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Create a full address to use with Google Maps
                            const additionalData = customer.additionalData || {};
                            let fullAddress = '';

                            if (additionalData?.full_address) {
                              fullAddress = additionalData.full_address;
                            } else if (customer.address) {
                              fullAddress = customer.address;
                            } else if (additionalData?.street_address) {
                              fullAddress = additionalData.street_address;
                            } else if (additionalData?.address_line1) {
                              fullAddress = additionalData.address_line1;
                              if (additionalData?.address_line2) {
                                fullAddress += `, ${additionalData.address_line2}`;
                              }
                            }

                            // Add postcode if not included
                            const postcode = additionalData?.postcode || customer.postcode;
                            if (postcode && !fullAddress.includes(postcode)) {
                              fullAddress += `, ${postcode}`;
                            }

                            if (fullAddress) {
                              openInGoogleMaps(fullAddress);
                            } else {
                              toast({
                                title: 'No address available',
                                description:
                                  "This customer doesn't have a complete address to show on the map",
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          <MapPin className="h-3 w-3" />
                          View on Google Maps
                        </Button>
                      </div>

                      {/* Calendar booking section */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium">Schedule Meeting</div>
                        <SimpleCalendar
                          customerId={customer.id}
                          customerName={customer.name}
                          customerEmail={customer.email}
                          customerPhone={customer.phone}
                          appointmentType="initialContact"
                          onDateSelected={(date) => {
                            // Handle successful booking
                            updateCustomerMutation.mutate({
                              id: customer.id,
                              updates: {
                                nextContactDate: date.toISOString(),
                                notes: customer.notes
                                  ? `${customer.notes}\n\nScheduled initial contact for ${format(date, 'PPpp')}`
                                  : `Scheduled initial contact for ${format(date, 'PPpp')}`,
                              },
                            });
                          }}
                        />
                      </div>

                      {/* Source and submission date */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium">Lead Information</div>
                        <div className="p-2 bg-slate-50 dark:bg-slate-800/60 rounded-md text-xs space-y-1">
                          {/* Source information */}
                          <div>Source: {customer.source || 'Direct'}</div>

                          {/* Submission date */}
                          {customer.createdAt && (
                            <div className="flex items-center gap-1">
                              <span>
                                Submitted:{' '}
                                {new Date(customer.createdAt).toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </span>
                            </div>
                          )}

                          {/* Contact preferences if available */}
                          {customer?.additionalData?.contactPreference && (
                            <div>
                              <div className="mb-1">Contact Preferences:</div>
                              <div className="flex flex-wrap gap-1">
                                {customer.additionalData.contactPreference
                                  .toLowerCase()
                                  .includes('email') && (
                                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs">
                                    Email
                                  </span>
                                )}
                                {customer.additionalData.contactPreference
                                  .toLowerCase()
                                  .includes('phone') && (
                                  <span className="px-1.5 py-0.5 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded text-xs">
                                    Phone
                                  </span>
                                )}
                                {customer.additionalData.contactPreference
                                  .toLowerCase()
                                  .includes('text') && (
                                  <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded text-xs">
                                    Text
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Property information section */}
                      {(customer?.additionalData?.property_type ||
                        customer?.additionalData?.bedrooms ||
                        customer?.additionalData?.homeowner_status) && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium">Property Information</div>
                          <div className="p-2 bg-slate-50 dark:bg-slate-800/60 rounded-md text-xs grid grid-cols-2 gap-1">
                            {customer?.additionalData?.property_type && (
                              <div>Type: {customer.additionalData.property_type}</div>
                            )}
                            {customer?.additionalData?.bedrooms && (
                              <div>Bedrooms: {customer.additionalData.bedrooms}</div>
                            )}
                            {customer?.additionalData?.homeowner_status && (
                              <div>Status: {customer.additionalData.homeowner_status}</div>
                            )}
                            {customer?.additionalData?.installation_timing && (
                              <div>Timeline: {customer.additionalData.installation_timing}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Notes section */}
                      {customer.notes && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium">Notes</div>
                          <div className="p-2 bg-slate-50 dark:bg-slate-800/60 rounded-md text-xs">
                            {customer.notes}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* For desktop */}
        {!isLoading && (
          <div className="hidden sm:block table-container">
            <Table className="table-modern">
              <TableHeader className="table-header">
                <TableRow>
                  <TableHead className="table-header-cell table-header-cell-accent">
                    Customer
                  </TableHead>
                  <TableHead className="table-header-cell">Email</TableHead>
                  <TableHead className="table-header-cell">Phone</TableHead>
                  <TableHead className="table-header-cell hidden md:table-cell">Address</TableHead>
                  <TableHead className="table-header-cell hidden xl:table-cell">Source</TableHead>
                  <TableHead className="table-header-cell hidden 2xl:table-cell">
                    Pipeline Stage
                  </TableHead>
                  <TableHead className="table-header-cell w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayCustomers?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-12 text-slate-500 dark:text-slate-400"
                    >
                      <div className="flex justify-center mb-3">
                        <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-full">
                          <Users className="h-6 w-6 text-slate-400 dark:text-slate-500" />
                        </div>
                      </div>
                      <p>No customers found</p>
                      <p className="text-sm mt-1">Try adjusting your search criteria</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayCustomers?.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className="table-row cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <TableCell className="table-cell table-cell-highlight flex items-center gap-2">
                        <Users className="h-4 w-4 accent-icon-indigo" />
                        {customer.name}
                      </TableCell>
                      <TableCell className="table-cell">
                        {customer.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                            <span>{customer.email}</span>
                            {/* Show email preference indicator if explicitly set */}
                            {(customer?.additionalData?.prefer_email === 'yes' ||
                              customer?.additionalData?.contactPreference === 'Email' ||
                              (customer?.additionalData?.contactPreference &&
                                customer.additionalData.contactPreference
                                  .toLowerCase()
                                  .includes('email'))) && (
                              <span className="inline-block px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-sm text-[10px] font-medium ml-1">
                                Preferred
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="table-cell">
                        {customer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                            <span>{customer.phone}</span>
                            {/* Show phone preference indicator if explicitly set */}
                            {(customer?.additionalData?.prefer_phone === 'yes' ||
                              customer?.additionalData?.contactPreference === 'Phone' ||
                              (customer?.additionalData?.contactPreference &&
                                (customer.additionalData.contactPreference
                                  .toLowerCase()
                                  .includes('phone') ||
                                  customer.additionalData.contactPreference
                                    .toLowerCase()
                                    .includes('call')))) && (
                              <span className="inline-block px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-sm text-[10px] font-medium ml-1">
                                Preferred
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="table-cell hidden md:table-cell">
                        {/* Always show address with improved display logic */}
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                          <span className="line-clamp-1">
                            {(() => {
                              // Check for additionalData
                              const additionalData = customer.additionalData || {};

                              // First check for full_address
                              if (additionalData?.full_address) {
                                const fullAddress = additionalData.full_address;

                                // Add postcode if not already in full address
                                if (customer.postcode && !fullAddress.includes(customer.postcode)) {
                                  return `${fullAddress}, ${customer.postcode}`;
                                }

                                return fullAddress;
                              }

                              // Build address from parts as fallback
                              let address = '';

                              // Start with best available address field
                              if (additionalData?.street_address) {
                                address = additionalData.street_address;
                              } else if (additionalData?.address_line1) {
                                address = additionalData.address_line1;

                                // Add address_line2 if available
                                if (additionalData?.address_line2) {
                                  address += `, ${additionalData.address_line2}`;
                                }
                              } else if (customer.address) {
                                address = customer.address;
                              } else {
                                return 'No address provided';
                              }

                              // Add postcode if available and not already in address
                              let postcodeToUse = additionalData?.postcode || customer.postcode;

                              if (postcodeToUse && !address.includes(postcodeToUse)) {
                                address += `, ${postcodeToUse}`;
                              }

                              return address;
                            })()}
                          </span>
                        </div>
                      </TableCell>
                      {/* Source - only show on larger screens */}
                      <TableCell className="table-cell hidden xl:table-cell">
                        {customer.source ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300">
                            {customer.source}
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500 text-sm">—</span>
                        )}
                      </TableCell>
                      {/* Pipeline Stage - only show on ultra-wide screens */}
                      <TableCell className="table-cell hidden 2xl:table-cell">
                        {customer.pipelineStageId ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                            {customer.pipelineStageId}
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500 text-sm">
                            New Lead
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="table-cell text-right py-0 px-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-2 text-xs rounded-full bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/customers/${customer.id}`);
                            }}
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            View details
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(customer);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete {customer.name}</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination && (
          <div className="mt-6">
            <PaginationControls
              currentPage={pagination.currentPage}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              limitOptions={pagination.limitOptions}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
              itemName="customers"
              isLoading={isFetching}
              showPageSize={true}
              showInfo={true}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-red-600 dark:text-red-500">Delete Customer</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the customer and all
              associated data.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="mb-4 text-sm">
              To confirm, type the customer name <strong>{customerToDelete?.name}</strong> below:
            </p>
            <Input
              className="mb-2"
              placeholder="Type customer name to confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
            />
            {deleteConfirmText !== customerToDelete?.name && deleteConfirmText.length > 0 && (
              <p className="text-sm text-red-600 mt-1">
                Name doesn't match. Please type the exact name.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setCustomerToDelete(null);
                setDeleteConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteConfirmText !== customerToDelete?.name || deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
